import React from 'react';
import {
  Coffee, Sparkles, Mic2, Users2, Star, Utensils, LayoutGrid,
  MessagesSquare, Handshake, PartyPopper, Download, ExternalLink, Gift, Store,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AGENDA as AGENDA_TIDS } from '@/constants/testIds';
import { AGENDA as AGENDA_DATA, AAWTA } from '@/lib/aawta';
import ShareMenu from './ShareMenu';
import AddToCalendar from './AddToCalendar';

const slugify = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const sessionAnchor = (item, index) => `session-${index + 1}-${slugify(item.title)}`;

const sessionShareUrl = (anchor) => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/#${anchor}`;
};

const sessionShareText = (item) => {
  const parts = [`AAWTA Workforce Summit · ${item.time}`, item.title];
  if (item.subtitle) parts.push(item.subtitle);
  parts.push('Thursday, September 17, 2026 · Stone Ridge Event Center, Stone Mountain, GA');
  return parts.join(' — ');
};

const ICONS = {
  Coffee, Sparkles, Mic2, Users2, Star, Utensils, LayoutGrid,
  MessagesSquare, Handshake, PartyPopper, Gift, Store,
};

const TAG_COLORS = {
  'Arrival': 'bg-slate-100 text-slate-700',
  'Opening': 'bg-aawta-magenta/10 text-aawta-magenta',
  'Keynote': 'bg-aawta-magenta text-white',
  'Panel': 'bg-aawta-navy text-white',
  'Break': 'bg-slate-100 text-slate-600',
  'Showcase': 'bg-amber-100 text-amber-800',
  'Networking': 'bg-emerald-100 text-emerald-800',
  'Working Session': 'bg-indigo-100 text-indigo-800',
  'Commitments': 'bg-aawta-magenta/15 text-aawta-magenta',
  'Closing': 'bg-aawta-navy/10 text-aawta-navy',
  'Reception': 'bg-purple-100 text-purple-800',
  'Raffle': 'bg-rose-100 text-rose-700',
};

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

export const Agenda = () => {
  return (
    <section id="agenda" data-testid={AGENDA_TIDS.section} className="py-24 md:py-32 bg-aawta-surface">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Full Agenda · September 17, 2026</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              A working day designed to move the workforce forward.
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              From opening keynote to closing commitments — every session is engineered to
              translate insight into action.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => openExternal(AAWTA.agendaPdfUrl)}
              data-testid={AGENDA_TIDS.downloadPdf}
              variant="outline"
              className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-5"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Agenda PDF
            </Button>
            <Button
              onClick={() => openExternal(AAWTA.eventbriteUrl)}
              className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-5 shadow-aawta-magenta"
            >
              Register Now
              <ExternalLink className="ml-2 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="mt-14 relative">
          {/* vertical spine */}
          <div className="absolute left-[22px] md:left-[26px] top-3 bottom-3 w-px bg-gradient-to-b from-aawta-magenta/40 via-aawta-border to-aawta-border" />

          <ol className="space-y-3">
            {AGENDA_DATA.map((item, i) => {
              const Icon = ICONS[item.icon] || Sparkles;
              const anchor = sessionAnchor(item, i);
              return (
                <li
                  key={i}
                  id={anchor}
                  data-testid={AGENDA_TIDS.item(i + 1)}
                  className="reveal relative scroll-mt-24"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="relative flex items-start gap-5 md:gap-6 bg-white rounded-2xl border border-aawta-border p-5 md:p-6 shadow-aawta-soft hover:shadow-aawta-elevated hover:-translate-y-0.5 transition-all duration-300">
                    <div className="shrink-0 relative z-10">
                      <div className="w-11 h-11 md:w-[52px] md:h-[52px] rounded-full bg-white border-2 border-aawta-magenta shadow-aawta-soft flex items-center justify-center">
                        <Icon className="h-5 w-5 md:h-6 md:w-6 text-aawta-magenta" strokeWidth={2} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display font-bold text-[15px] md:text-base text-aawta-navy tracking-tight">
                          {item.time}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${
                            TAG_COLORS[item.tag] || 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {item.tag}
                        </span>
                      </div>
                      <h3 className="mt-1.5 font-display font-semibold text-lg md:text-xl text-aawta-navy leading-snug">
                        {item.title}
                      </h3>
                      {item.subtitle && (
                        <p className="mt-1 text-[13.5px] leading-relaxed text-aawta-muted">
                          {item.subtitle}
                        </p>
                      )}
                      {item.concurrent && (
                        <span className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <Sparkles className="h-3 w-3" /> Concurrent activity
                        </span>
                      )}
                    </div>
                    <div className="shrink-0 self-start md:self-center flex flex-col md:flex-row items-end md:items-center gap-1.5 md:gap-2">
                      <AddToCalendar
                        time={item.time}
                        title={`${item.title} — AAWTA Workforce Summit`}
                        description={[item.subtitle, `${item.tag} · ${item.time} Eastern`, 'AAWTA Building the Future Workforce Summit'].filter(Boolean).join('\n')}
                        url={sessionShareUrl(anchor)}
                        testIdPrefix={`session-calendar-${i + 1}`}
                        buttonLabel=""
                        compact
                      />
                      <ShareMenu
                        title={`${item.title} · AAWTA Workforce Summit`}
                        text={sessionShareText(item)}
                        url={sessionShareUrl(anchor)}
                        emailSubject={`${item.title} — AAWTA Workforce Summit (${item.time})`}
                        emailBody={`${sessionShareText(item)}\n\nView the session:`}
                        testIdPrefix={`session-share-${i + 1}`}
                        buttonLabel=""
                        compact
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-14 p-6 md:p-8 rounded-3xl bg-white border border-aawta-border flex flex-col md:flex-row items-center justify-between gap-6 shadow-aawta-soft">
          <div>
            <div className="font-display font-bold text-xl text-aawta-navy">
              Full agenda subject to minor refinements.
            </div>
            <p className="text-aawta-muted mt-1 text-sm">
              Session details, speakers, and breakout topics will be published as they are
              confirmed.
            </p>
          </div>
          <Button
            onClick={() => openExternal(AAWTA.eventbriteUrl)}
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-5 shadow-aawta-magenta"
          >
            Register Now
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Agenda;
